/**
 * Versioning & maintenance policy (design D7; av-methodology-domain spec).
 *
 * `manifest.json` records the methodology's own version history
 * (2010 origin → 2018 technology review → 2026 current revision) and a
 * maintenance policy that keeps scientific-core changes on manual review
 * distinct from technology-catalog changes on a periodic one. This module is
 * the typed accessor other code should use instead of reading the raw JSON
 * shape directly.
 */

import { manifest } from "./data-source";
import type { ManifestVersion } from "./types";

export type ContentKind = "methodology" | "technology";

export function listVersions(): ManifestVersion[] {
  return manifest.versions;
}

export function currentVersion(): ManifestVersion {
  const current = manifest.versions.find((v) => v.status === "current");
  if (!current) throw new Error("manifest.json: no version marked status \"current\".");
  return current;
}

export function historicalVersions(): ManifestVersion[] {
  return manifest.versions.filter((v) => v.status === "historical");
}

/**
 * Which review policy applies to a given kind of change — methodology
 * (manual, author-driven) vs. technology (periodic). Callers should consult
 * this rather than assume; it keeps `CLAUDE.md`'s "mudanças de software não
 * implicam mudanças na metodologia" enforceable in code, not just in docs.
 */
export function maintenancePolicyFor(kind: ContentKind): string {
  return kind === "methodology" ? manifest.maintenance.methodologyReview : manifest.maintenance.technologyReview;
}

export function technologyReviewIntervalMonths(): number {
  return manifest.maintenance.technologyReviewIntervalMonths;
}

/**
 * Whether a technology review has actually taken place yet. `manifest.json`
 * intentionally leaves these `null` until a real review occurs (design D14) —
 * never pre-filled with a plausible-looking date.
 */
export function lastTechnologyReview(): string | null {
  return manifest.maintenance.lastTechnologyReview;
}

export function nextTechnologyReview(): string | null {
  return manifest.maintenance.nextTechnologyReview;
}
